import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Lock, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/Sheet';
import { useClients } from '@/hooks/useClients';
import { useLockMonthPreview, useLockMonth } from '@/hooks/useClosings';
import { extractErrorMessage } from '@/lib/api';

interface LockMonthDialogProps {
  open: boolean;
  onClose: () => void;
}

function defaultPeriod(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function LockMonthDialog({ open, onClose }: LockMonthDialogProps) {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<string>(defaultPeriod());
  const [clientId, setClientId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ tripsLocked: number; deductionsApplied: number } | null>(null);
  const [overrideReason, setOverrideReason] = useState<string>('');

  useEffect(() => { if (open) { setError(null); setSuccess(null); } }, [open]);

  const { data: clientsData } = useClients({ status: 'active', pageSize: 100 });
  const clients = clientsData?.items ?? [];

  const previewInput = useMemo(() => period ? { period, clientId: clientId || undefined } : null,
    [period, clientId]);
  const { data: preview, isLoading: previewLoading } = useLockMonthPreview(previewInput);
  const lockMutation = useLockMonth();

  const handleLock = async () => {
    setError(null);
    setSuccess(null);
    try {
      const result = await lockMutation.mutateAsync({ period, clientId: clientId || undefined, overrideReason: overrideReason.trim() || undefined } as any);
      setSuccess({ tripsLocked: result.tripsLocked, deductionsApplied: result.deductionsApplied });
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-indigo-500" />
            {t('closings.lockMonth.title')}
          </SheetTitle>
          <SheetDescription>{t('closings.lockMonth.description')}</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Period picker */}
          <div className="space-y-1.5">
            <Label htmlFor="lm-period">{t('closings.field.period')} *</Label>
            <Input
              id="lm-period" type="month"
              value={period} onChange={(e) => setPeriod(e.target.value)}
              disabled={!!success}
            />
            <p className="text-xs text-muted-foreground">{t('closings.field.periodHint')}</p>
          </div>

          {/* Client picker */}
          <div className="space-y-1.5">
            <Label htmlFor="lm-client">{t('closings.field.client')}</Label>
            <select
              id="lm-client"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              disabled={!!success}
              className="w-full h-10 px-3 rounded border border-border bg-background"
            >
              <option value="">{t('closings.field.allClients')}</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} — {c.nameEn}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">{t('closings.field.clientHint')}</p>
          </div>

          {/* Preview card */}
          {!success && preview && (
            <div className="rounded-lg border border-border bg-card p-4 space-y-2">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{t('closings.preview.title')}</div>
              <div className="text-sm space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('closings.preview.tripsToLock')}:</span>
                  <span className="font-medium tabular-nums">{preview.tripsToLock}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('closings.preview.deductionsToApply')}:</span>
                  <span className="font-medium tabular-nums">{preview.deductionsToApply}</span>
                </div>
                {(preview as any).overduePhotos && (preview as any).overduePhotos.length > 0 && (
                  <div className="flex justify-between text-rose-600">
                    <span>{t('closings.preview.overduePhotos')}:</span>
                    <span className="font-medium tabular-nums">{(preview as any).overduePhotos.length}</span>
                  </div>
                )}
              </div>
              {preview.tripsToLock === 0 && preview.deductionsToApply === 0 && (
                <p className="text-xs text-muted-foreground italic pt-2 border-t border-border">
                  {t('closings.preview.nothingToLock')}
                </p>
              )}
            </div>
          )}

          {!success && (preview as any)?.blocked && (
            <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-4 space-y-2">
              <div className="font-medium text-rose-700 text-sm">{t('closings.preview.blockedByOverdue')}</div>
              <div className="text-xs text-muted-foreground">{t('closings.preview.overdueList')}:</div>
              <ul className="text-sm space-y-1 max-h-32 overflow-y-auto">
                {((preview as any).overduePhotos as Array<{code: string; daysOverdue: number}>).map((t) => (
                  <li key={t.code} className="flex justify-between">
                    <span className="font-mono text-xs">{t.code}</span>
                    <span className="text-rose-600 text-xs">+{t.daysOverdue}d overdue</span>
                  </li>
                ))}
              </ul>
              <div className="pt-2 border-t border-rose-500/30">
                <Label htmlFor="lm-override" className="text-xs">{t('closings.field.overrideReason')} *</Label>
                <Input
                  id="lm-override"
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder={t('closings.field.overrideReasonPlaceholder')}
                  maxLength={500}
                />
              </div>
            </div>
          )}

          {/* Warning */}
          {!success && (
            <div className="flex items-start gap-2 p-3 rounded bg-amber-500/10 border border-amber-500/30 text-amber-700 text-sm">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{t('closings.lockMonth.warning')}</span>
            </div>
          )}

          {/* Success state */}
          {success && (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm space-y-2">
              <div className="flex items-center gap-2 font-medium text-emerald-700">
                <CheckCircle2 className="h-5 w-5" />
                {t('closings.lockMonth.success')}
              </div>
              <div className="space-y-1 text-muted-foreground">
                <div>{t('closings.preview.tripsToLock')}: <span className="font-medium text-foreground">{success.tripsLocked}</span></div>
                <div>{t('closings.preview.deductionsToApply')}: <span className="font-medium text-foreground">{success.deductionsApplied}</span></div>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 p-3 rounded bg-destructive/10 border border-destructive/30 text-destructive text-sm">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="border-t border-border p-4 flex items-center justify-end gap-2 shrink-0">
          {success ? (
            <Button onClick={onClose}>{t('common.close')}</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose} disabled={lockMutation.isPending}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleLock}
                disabled={lockMutation.isPending || previewLoading || (preview?.tripsToLock === 0 && preview?.deductionsToApply === 0)}
              >
                {lockMutation.isPending
                  ? <><Loader2 className="h-4 w-4 me-2 animate-spin" /> {t('closings.lockMonth.locking')}</>
                  : <><Lock className="h-4 w-4 me-2" /> {t('closings.lockMonth.confirm')}</>
                }
              </Button>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
